/**
 * Help Center content — one section per admin area. Kept as plain data so
 * guides can be edited without touching UI. `path` powers the "Open …" button
 * and the per-page "How this works" link (matched on the first URL segment).
 */
export const HELP_SECTIONS = [
  {
    id: "getting-started",
    label: "Getting started",
    path: "/admin",
    summary: "Set up your studio in about fifteen minutes and go live.",
    articles: [
      {
        id: "first-steps",
        title: "Your first 15 minutes",
        steps: [
          "Open Site Settings → Branding and upload your logo, pick a template and palette. This styles your public site, booking page, galleries and emails.",
          "Add at least one package in Services — it appears on your booking page immediately.",
          "Set your working days and buffer in Site Settings → Booking, and block holidays in Availability.",
          "Enter your notification email in Site Settings → Booking so inquiries, bookings and selections reach the right inbox.",
          "Click View site in the sidebar to check the result, then share your link (or connect your own domain).",
        ],
        tips: ["The Overview page shows a checklist until these are done — each item links to the right screen."],
      },
      {
        id: "notifications",
        title: "Where alerts go (email, push, bell)",
        steps: [
          "Email alerts go to Site Settings → Booking → Notification email; if empty, to the studio owner's login email.",
          "Turn on push notifications from Overview → Push notifications → Enable on this device (install the app to your phone first for the best experience).",
          "The bell in the header collects everything in-app. Click an item to jump straight to it.",
        ],
        tips: ["Every alert is scoped to your studio only — you will never receive another studio's activity."],
      },
    ],
  },
  {
    id: "bookings",
    label: "Bookings",
    path: "/admin/bookings",
    summary: "Requests from your booking page, confirmations, reminders and reschedules.",
    articles: [
      {
        id: "confirm",
        title: "Confirm, reschedule or decline a booking",
        steps: [
          "New requests appear as Pending. Open the row to see the client's details, package and notes.",
          "Click Confirm to lock the slot — the client gets a confirmation email and the date is auto-blocked in Availability.",
          "Use Reschedule to propose a new time; Decline releases the slot and notifies the client politely.",
          "Mark Completed after the session so it counts toward revenue and unlocks the review request.",
        ],
        tips: ["Automatic 24-hour session reminders are on by default — you can see the log and run a check now from Overview → Auto session reminders."],
      },
      {
        id: "deposit",
        title: "Take a deposit or full payment",
        steps: [
          "Create an invoice from the booking row (Invoice → New from booking) — the package price is pre-filled.",
          "Send it; the client pays by card from the email link.",
          "Paid invoices update the booking and show in Revenue on Overview.",
        ],
      },
    ],
  },
  {
    id: "availability",
    label: "Availability",
    path: "/admin/availability",
    summary: "Control which days and times clients can book.",
    articles: [
      {
        id: "block",
        title: "Block days off",
        steps: [
          "Pick a start and end date, add an optional note (e.g. 'Wedding — Lagos') and click Block.",
          "Blocked ranges disappear from the public booking calendar instantly.",
          "Bookings you confirm are blocked automatically and are removed if the booking is cancelled.",
        ],
        tips: ["Working hours, slot length and buffer time live in Site Settings → Booking."],
      },
    ],
  },
  {
    id: "invoices",
    label: "Invoices",
    path: "/admin/invoices",
    summary: "Bill clients, get paid by card, and keep the ledger tidy.",
    articles: [
      {
        id: "create",
        title: "Create and send an invoice",
        steps: [
          "Click New invoice, choose the client (or type a new one) and add line items — packages, prints, travel.",
          "Set a due date. Unpaid invoices get an automatic reminder 24h before due and are marked Overdue after.",
          "Click Send — the client receives a branded email with a Pay button and a PDF attached.",
          "Download PDF at any time; on mobile it opens the share sheet so you can save to Files or AirDrop.",
        ],
        tips: ["Payouts, currency and tax settings are in Site Settings → Booking → Payments."],
      },
      {
        id: "paid",
        title: "Record an offline payment",
        steps: ["Open the invoice and choose Mark as paid when a client pays by transfer or cash — the ledger and Overview update immediately."],
      },
    ],
  },
  {
    id: "contracts",
    label: "Contracts",
    path: "/admin/contracts",
    summary: "Templates, e-signatures and signed agreements in one folio.",
    articles: [
      {
        id: "template",
        title: "Write a reusable template",
        steps: [
          "Open Templates & agreements → New template and paste your agreement text.",
          "Use placeholders {{client_name}}, {{date}} and {{total}} — they fill in automatically when you draft from a booking.",
          "Save. New contracts can now start from this template.",
        ],
      },
      {
        id: "sign",
        title: "Send for e-signature",
        steps: [
          "Create a contract (from a booking or blank), review the text and click Send for signature.",
          "The client signs on their phone from the email link; you get an alert and the signed PDF is stored with the contract.",
          "Use Void if terms change — the old link stops working and you can send a fresh one.",
        ],
      },
    ],
  },
  {
    id: "inquiries",
    label: "Inquiries",
    path: "/admin/inquiries",
    summary: "Every contact-form message, replied to and converted into bookings.",
    articles: [
      {
        id: "reply",
        title: "Reply and track a lead",
        steps: [
          "New inquiries show a red badge in the sidebar and land at the top of the list in bold.",
          "Open one, write your reply and click Send reply — it goes from your studio's email identity with the client's message quoted underneath.",
          "Tick 'Include a Book your session button' to add a direct link to your booking page.",
          "Move the lead along the pipeline: New → Contacted → Booked or Lost. Notes are private to your studio.",
          "Click Add to clients to create the customer record in one tap.",
        ],
        tips: [
          "Clients answer through a private reply page linked in your email — their response appears in the same thread and you're alerted.",
          "If a lead has waited 24 hours without a reply, you get a nudge by email, push and in the bell.",
        ],
      },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    path: "/admin/customers",
    summary: "Everyone who has booked, paid or written to you.",
    articles: [
      {
        id: "records",
        title: "How customer records are created",
        steps: [
          "A record is created automatically on the first confirmed booking, and when you click Add to clients on an inquiry.",
          "Open a customer to see their bookings, invoices, galleries and last activity in one place.",
        ],
      },
    ],
  },
  {
    id: "reviews",
    label: "Reviews",
    path: "/admin/reviews",
    summary: "Collect testimonials and publish them on your site.",
    articles: [
      {
        id: "request",
        title: "Request and publish a review",
        steps: [
          "After a session is Completed, use Request review from the booking — the client gets a one-tap link.",
          "New reviews arrive with a star rating and appear here (and alert you) — nothing is public yet.",
          "Toggle Publish on the ones you love; they show in the testimonials section of your site.",
        ],
      },
    ],
  },
  {
    id: "galleries",
    label: "Galleries",
    path: "/admin/galleries",
    summary: "Deliver proofs, collect favorites, design the experience, and hand over finals.",
    articles: [
      {
        id: "create",
        title: "Create a gallery and upload photos",
        steps: [
          "Galleries → New collection. Give it a name, the client's name and email, and a view password.",
          "Drag photos into the Photos tab (or tap to select on mobile). Uploads run in the background — you can keep working.",
          "Optional: set Category tags, a cover photo and Watermark previews in Settings.",
          "Share the link from the header. Clients need the view password to open it.",
        ],
        tips: ["Large batches are optimised as they upload; thumbnails appear first and full-size variants finish in the background."],
      },
      {
        id: "design",
        title: "Design the look (Design tab & Live preview)",
        steps: [
          "Pick a template: Editorial (magazine spreads), Mosaic (masonry) or Cinema (dark, single column with slideshow).",
          "Choose a font pairing and accent colour, or click AI Suggest to let Lumetier read the photos and propose a look.",
          "Click Live preview to open the Design Studio — changes apply instantly in the preview as you edit. Use the 24/60/All control to choose how many photos the preview renders.",
          "Cover design lets you pick the hero photo, focal point and title treatment.",
        ],
      },
      {
        id: "favorites",
        title: "Client favorites & selections",
        steps: [
          "In Settings, set Included edits (from the package), Extra edit price and Max selections if you want a cap.",
          "Clients heart their favorites and click Submit; you get an email, push and bell alert, and the gallery moves to Selections received.",
          "Open the Favorites tab to see the picked photos, extra-edit count and download the list.",
          "If they picked more than included, the extra-edit agreement is recorded automatically.",
        ],
      },
      {
        id: "deliver",
        title: "Deliver finals & downloads",
        steps: [
          "Upload the edited photos (replace or add), then set a Download password in Settings and click Mark as delivered.",
          "Clients download individual photos or the whole set as a zip from the gallery.",
          "Use Auto-expire date to close the gallery after a period; you can reopen it any time.",
        ],
      },
      {
        id: "collaborate",
        title: "Collaborators, activities & live sessions",
        steps: [
          "Collaborators tab: invite a partner or family member by email — they get their own favorites and can leave comments.",
          "Activities shows every view, favorite and comment with timestamps.",
          "Live session opens a shared room where you and the client browse the gallery together in real time.",
        ],
      },
    ],
  },
  {
    id: "services",
    label: "Services",
    path: "/admin/services",
    summary: "Packages, pricing and durations shown on your booking page.",
    articles: [
      {
        id: "packages",
        title: "Add or edit a package",
        steps: [
          "Click New service, name it (e.g. 'Maternity — Signature') and add a short description.",
          "Add pricing tiers with duration and what's included. The first tier is shown as the 'from' price.",
          "Reorder with the arrows — the order is what clients see. Toggle a service off to hide it without deleting.",
        ],
        tips: ["Included edits on a package pre-fill the gallery selection limits for bookings made with it."],
      },
    ],
  },
  {
    id: "site",
    label: "Site Settings",
    path: "/admin/site",
    summary: "Branding, homepage, portfolio, booking rules, domain and tools.",
    articles: [
      {
        id: "branding",
        title: "Branding & templates",
        steps: [
          "Branding: upload logo and favicon, pick a site template, fonts and palette. The live preview on the right updates as you go.",
          "Homepage: hero image, headline, intro copy and call-to-action.",
          "Portfolio: curate the images that appear on your public site; drag to reorder.",
        ],
      },
      {
        id: "domain",
        title: "Connect your own domain",
        steps: [
          "Site Settings → Custom domain: type your domain (e.g. studio.com) and click Connect.",
          "Add the DNS records shown at your registrar. We check automatically every few minutes and email you when it goes live.",
          "Until then your site works at your Lumetier address (View site in the sidebar).",
        ],
      },
      {
        id: "booking-rules",
        title: "Booking rules & payments",
        steps: [
          "Booking tab: working days, hours, slot length, buffer, lead time and the notification email.",
          "Payments: connect card payments and set currency and deposit rules.",
        ],
      },
      {
        id: "tools",
        title: "Tools: backup & sync, featured listing",
        steps: [
          "Tools → Backup & sync moves services, portfolio and copy between environments.",
          "Featured on Lumetier lists your studio on the public showcase with a backlink to your site.",
        ],
      },
    ],
  },
  {
    id: "account",
    label: "Account & billing",
    path: "/admin/profile",
    summary: "Your login, appearance, plan and team.",
    articles: [
      {
        id: "profile",
        title: "Profile, password & appearance",
        steps: [
          "Open your avatar (top right) → Profile to change your name, password and light/dark appearance.",
          "Forgot your password? Use 'Forgot password' on the login page — a reset link arrives by email.",
        ],
      },
      {
        id: "billing",
        title: "Plan & billing",
        steps: [
          "Avatar → Billing shows your current plan, storage and invoices from Lumetier.",
          "Upgrade or change plan any time; changes apply immediately.",
        ],
      },
    ],
  },
];

/** Section for a given admin pathname (first segment after /admin). */
export function helpSectionForPath(pathname) {
  const seg = (pathname || "").replace(/^\/admin\/?/, "").split("/")[0];
  const map = { "": "getting-started", profile: "account", billing: "account", "gallery-clients": "customers" };
  const id = map[seg] ?? seg;
  return HELP_SECTIONS.find((s) => s.id === id) || null;
}
